/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    ActionGroup,
    ActionKernelType,
    ResumeFromMyShowsActionKernel,
    WatchFromMyShowsActionKernel,
    WatchFromVodActionKernel,
} from 'client-code/actions';
import {
    CollectionType,
    MyShowsItemAttributeType,
    MyShowsTypeV2,
    QuickAction,
} from 'client-code/myshows';

import { InteractiveComponents } from '../../../core/enums';
import { ContentType } from '../../../core/enums/ContentType';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import { IInfoCard, InfoCardManager } from '../../../core/infocard';
import { ServicesClientImpl, VodInteractor } from '../../../core/interactors';
import { Log } from '../../../core/logging';
import { KeyManager, ScreenTaskControllerTypes } from '../../../core/navigation';
import type { AppDispatch, RootState, TGetState } from '../../../core/tivo/tivo-redux-wrapper';
import {
    TAction,
    TActionList,
    TMyShowsItemList,
    TVodPlayActionStateType,
    VodRequiredParams,
} from '../../../core/types';
import { MyShowItemType } from '../../../core/types/TMyShowsItemList';
import { MyShowsInfoCardUserInputHandler } from '../../../core/usecases';
import { ListTypes } from '../../../core/utils';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { setActiveInteractiveComponent } from '../../interactivelayer/interactiveLayer.reducers';
import { setListEntryFocus, setListEntryPosition } from '../../list/list.actions';
import { onMouseOutList, onMouseOverList } from '../../list/list.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showPCPinOverlayWithProgramRating,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onKeyOkVodPlayErrorOverlay,
    onMouseClickVodPlayErrorOverlay,
} from '../../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import {
    onKeyOkVodUpSellOverlay,
    onMouseClickVodUpSellOverlay,
} from '../../overlay/vodupselloverlay/vodupselloverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../../overlaydialog/overlaydialog.reducers';
import {
    collectionScreenTransition,
    programScreenTransition,
} from '../../screens/navigation/screensnavigation';
import { notifyVodPlayActionState } from '../../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    setMyShowsCaretVisibleAction,
    setMyShowsErrorAction,
    setMyShowsItemsAction,
    setMyShowsItemsLoadingAction,
} from '../myshows.actions';
import {
    getItemsListHiglightPosition,
    getMyShowsItems,
    onKeyBackMyShowsOperations,
    onKeyDownMyShowsOperation,
    onKeyLeft,
    onKeyRight,
    onKeyUpMyShowsOperations,
    setLastSelectedItem,
    setMyShowsPage,
    triggerPlayBackForMyShowsItem,
} from '../myshows.reducers';
import { MyShowsListGenerator } from '../MyShowsListGenerator';

const TAG: string = 'MyShowsScreenNavigation';

const MAX_ITEM_IN_PAGE = 13;

const myShowsItemsObj: TMyShowsItemList = {
    lastSelectedItem: {
        index: 0,
        myShowsItemId: '',
    },
    myShows: [],
    sortOrder: '',
    totalMyShowsCount: 0,
};

const findPlayFromVodAction = async (
    currentItem,
): Promise<{ contentType: MyShowsTypeV2; action?: TAction }> => {
    let actions: TActionList = { actionList: { actions: [] } };
    const itemType = currentItem.myShowsType;
    const vodInteractor = new VodInteractor();
    switch (itemType) {
        case MyShowsTypeV2.MyShowsRecordingV2:
            actions = await vodInteractor.getRecordingActionsV2({
                recordingId: currentItem.recordingId || '',
                groups: [ActionGroup.Watch],
                screenType: 'MyShows',
            });
            break;

        case MyShowsTypeV2.MyShowsContentV2:
            actions = await vodInteractor.getContentActions({
                contentId: currentItem.contentId || '',
                groups: [ActionGroup.Watch],
                screenType: 'MyShows',
            });
            break;

        case MyShowsTypeV2.MyShowsFolderV2:
            actions = await vodInteractor.getUnifiedCollectionActions({
                unifiedCollectionId: currentItem.unifiedCollectionId!,
                groups: [ActionGroup.Watch],
                screenType: 'MyShows',
            });
            break;

        default:
    }

    const actionWithVodPlayActionKernel = actions?.actionList.actions.find(
        (item) => item.kernel.actionKernelType === ActionKernelType.WatchFromVodActionKernel,
    );

    return { action: actionWithVodPlayActionKernel, contentType: itemType };
};

const createVodRequiredParams = (
    currentItem: MyShowItemType,
    action: TAction,
    itemType: 'offer' | 'content' | 'unifiedCollection',
): VodRequiredParams => {
    const kernel = action.kernel as unknown as WatchFromVodActionKernel;

    const baseParams = {
        partnerId: kernel.partnerId,
        contentSupplierPartnerId: kernel.contentSupplierPartnerId,
    };

    if (itemType === 'content') {
        return {
            ...baseParams,
            itemType,
            collapsedVodItemId: kernel.collapsedVodItemId || '',
            id: kernel.contentId,
        };
    }
    if (itemType === 'unifiedCollection') {
        return {
            ...baseParams,
            itemType,
            collapsedVodItemId: kernel.collapsedVodItemId || '',
            id: currentItem.unifiedCollectionId || '',
        };
    }
    return {
        itemType,
        ...baseParams,
        id: currentItem.recordingId || '',
    };
};

const handleVodPlayAction =
    (currentItem: MyShowItemType, action: TAction, contentType: MyShowsTypeV2) =>
    (dispatch: Function) => {
        switch (contentType) {
            case MyShowsTypeV2.MyShowsRecordingV2:
                dispatch(
                    notifyVodPlayActionState({
                        type: TVodPlayActionStateType.INIT,
                        vodRequiredParams: createVodRequiredParams(currentItem, action, 'offer'),
                    }),
                );
                break;
            case MyShowsTypeV2.MyShowsContentV2:
                dispatch(
                    notifyVodPlayActionState({
                        type: TVodPlayActionStateType.INIT,
                        vodRequiredParams: createVodRequiredParams(currentItem, action, 'content'),
                    }),
                );
                break;
            case MyShowsTypeV2.MyShowsFolderV2:
                dispatch(
                    notifyVodPlayActionState({
                        type: TVodPlayActionStateType.INIT,
                        vodRequiredParams: createVodRequiredParams(
                            currentItem,
                            action,
                            'unifiedCollection',
                        ),
                    }),
                );
                break;
            default:
                Log.e(
                    TAG,
                    'Cannot play item as unknown MyShowsTypeV2 from VodPlayAction',
                    `contentType : ${contentType}`,
                );
                KeyManager.getInstance().stopKeyIgnoring();
        }
    };

export const onKeyBackMyShows = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
            break;
        case ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS:
            dispatch(onKeyBackMyShowsOperations());
            break;
        default:
            break;
    }
};

export const onKeyUpMyShows = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS:
            dispatch(onKeyUpMyShowsOperations());
            break;
        default:
            break;
    }
};

export const onKeyDownMyShows = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS:
            dispatch(onKeyDownMyShowsOperation());
            break;
        default:
            break;
    }
};

export const onKeyRightMyShows = () => (dispatch: Function, getState: Function) => {
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
        screens: { screenTaskController },
    } = getState();
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS:
            if (filterList.isFocused && itemList.limit > 0) {
                dispatch(onKeyRight());
            }
            break;
        default:
            break;
    }
};

export const onKeyLeftMyShows = () => (dispatch: Function, getState: Function) => {
    const {
        list: { [ListTypes.MYSHOWS_ITEMS]: itemList },
        screens: { screenTaskController },
    } = getState();
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;
        default:
            if (itemList.isFocused) {
                dispatch(onKeyLeft());
            }
            break;
    }
};

export const onKeyPlayMyShows = () => async (dispatch: Function, getState: Function) => {
    const {
        myShows: {
            myShowsItems: { myShows },
        },
        list: { [ListTypes.MYSHOWS_ITEMS]: itemList },
        screens: { screenTaskController },
    } = getState() as RootState;

    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS) return;

    if (itemList.isFocused) {
        const currentItem = myShows[itemList.position];
        const isPlayable = currentItem.quickActions?.includes(QuickAction.Play);

        if (!isPlayable) return;

        const services = new ServicesClientImpl();
        let actions: TActionList = { actionList: { actions: [] } };

        KeyManager.getInstance().startKeyIgnoring();

        const isVodPlayEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_VOD_PLAY_ACTION_REMOVE_IN_KST_12288,
        );

        if (isVodPlayEnabled) {
            const { action, contentType } = await findPlayFromVodAction(currentItem);

            if (action) {
                /**
                 * My Shows item can have both vod as well as recording to play.
                 * priority is given to vod play if both are present.
                 * if vod play action is found, return from here.
                 * else continue with recording play flow.
                 */
                dispatch(handleVodPlayAction(currentItem, action, contentType));
                return;
            }
        }

        try {
            if (currentItem.unifiedCollectionId) {
                actions = await services.getUnifiedCollectionActions({
                    unifiedCollectionId: currentItem.unifiedCollectionId!,
                    groups: [ActionGroup.Watch],
                    screenType: 'MyShows',
                });
            } else if (currentItem.recordingId) {
                actions = await services.getRecordingActionsV2({
                    recordingId: currentItem.recordingId,
                    groups: [ActionGroup.Watch],
                    screenType: 'MyShows',
                });
            } else {
                Log.e(TAG, 'Cannot play item as unifiedCollectionId and recordingId are undefined');
                KeyManager.getInstance().stopKeyIgnoring();
                return;
            }
        } catch (_error) {
            const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage);
            KeyManager.getInstance().stopKeyIgnoring();
            return;
        }

        const actionWithMyshowActionKernel = actions.actionList.actions.find(
            (item) =>
                item.kernel.actionKernelType === ActionKernelType.WatchFromMyShowsActionKernel ||
                item.kernel.actionKernelType === ActionKernelType.ResumeFromMyShowsActionKernel,
        );

        if (actionWithMyshowActionKernel === undefined) {
            Log.e(TAG, 'Cannot play item as both MyShowsActionKernel  missing in actionList');
            Diagnostics.error(
                TAG,
                'Cannot play item as both MyShowsActionKernel  missing in actionList',
            );
            KeyManager.getInstance().stopKeyIgnoring();
            return;
        }

        const { internalRatingList = [], contentType } =
            actionWithMyshowActionKernel.kernel as unknown as
                | WatchFromMyShowsActionKernel
                | ResumeFromMyShowsActionKernel;

        const isRestricted = services.checkPCViolationsWithRatings(
            internalRatingList,
            currentItem.attribute.includes(MyShowsItemAttributeType.IsAdult),
            contentType,
        );

        if (isRestricted) {
            const ratingName = services.getRestrictedRatingName(internalRatingList);

            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, false));
            // pc pin challenge
            dispatch(
                showPCPinOverlayWithProgramRating(
                    () => triggerPlayBackForMyShowsItem(currentItem),
                    currentItem.attribute.includes(MyShowsItemAttributeType.IsAdult),
                    ratingName,
                ),
            );
        } else {
            dispatch(triggerPlayBackForMyShowsItem(currentItem));
        }
    }
};

const transitionToSeriesOrProgramScreen =
    (focusedItem: MyShowItemType, selectedFilter: string) => (dispatch: Function) => {
        const { collectionType } = focusedItem;
        const isNonFolderRecordingInPausedFilter =
            focusedItem.myShowsItemId.startsWith('tivo:crc.') &&
            !focusedItem.myShowsType.includes('Folder') &&
            selectedFilter === 'paused';

        if (
            collectionType === CollectionType.Movie ||
            collectionType === CollectionType.Special ||
            isNonFolderRecordingInPausedFilter
        ) {
            let id = focusedItem.myShowsItemId;
            let type = ContentType.RECORDING_ID;

            if (focusedItem.recordingId) {
                id = focusedItem.recordingId;
                type = ContentType.RECORDING_ID;
            } else if (focusedItem.contentId) {
                id = focusedItem.contentId;
                type = ContentType.CONTENT_ID;
            }

            dispatch(programScreenTransition({ id, contentType: type }, showWhisper));
        } else if (focusedItem.unifiedCollectionId && collectionType === CollectionType.Series) {
            dispatch(
                collectionScreenTransition(
                    {
                        unifiedCollectionId: focusedItem.unifiedCollectionId,
                        transitionScreen: TransitionScreenType.MYSHOWS,
                    },
                    showWhisper,
                ),
            );
        } else {
            Log.e(TAG, 'Cannot transition to next screen.');
            Diagnostics.error(TAG, 'Cannot transition to next screen.');
        }
    };

const handleDefaultMyShowsTasksKeyOk =
    (filterList, itemList, myShowsItems, myShowsFilter) => async (dispatch: Function) => {
        if (filterList.isFocused && itemList.limit > 0) {
            dispatch(onKeyRight());
        } else if (itemList.isFocused) {
            const focusedItem = myShowsItems.myShows[itemList.position];
            const selectedFilter = myShowsFilter[filterList.position].value;

            KeyManager.getInstance().startKeyIgnoring();

            await dispatch(setLastSelectedItem());

            const services = new ServicesClientImpl();
            const isAdultHidden = services.shouldHideMetadata();

            if (focusedItem.attribute.includes(MyShowsItemAttributeType.IsAdult) && isAdultHidden) {
                dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, false));
                // pc pin challenge
                dispatch(
                    showPCPinOverlayWithProgramRating(
                        () => transitionToSeriesOrProgramScreen(focusedItem, selectedFilter),
                        focusedItem.attribute.includes(MyShowsItemAttributeType.IsAdult),
                    ),
                );
            } else {
                dispatch(transitionToSeriesOrProgramScreen(focusedItem, selectedFilter));
            }
        }
    };

export const onKeyOkMyShows = () => async (dispatch: Function, getState: Function) => {
    const {
        list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
        myShows: { myShowsItems, myShowsFilter },
        screens: { screenTaskController },
    } = getState() as RootState;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onKeyOkVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyOkVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS:
            dispatch(
                handleDefaultMyShowsTasksKeyOk(filterList, itemList, myShowsItems, myShowsFilter),
            );
            break;
        default:
            break;
    }
};

export const onKeyDigitMyShows = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyDigitPcPinOverlay());
};

export const onKeyChannelUpMyShows = () => async (dispatch: Function, getState: TGetState) => {
    const state = getState();
    const filterList = state.list[ListTypes.MYSHOWS_FILTER];
    const { page, myShowsItems, isPageLoading } = state.myShows;
    const { screenTaskController } = state.screens;

    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS) return;
    if (isPageLoading || page === 0 || filterList.isFocused) return;

    await dispatch(setMyShowsPage(page - 1));
    const highLightIndex = getItemsListHiglightPosition('up', myShowsItems.totalMyShowsCount);
    dispatch(setListEntryPosition(ListTypes.MYSHOWS_ITEMS, highLightIndex));
};

export const onKeyChannelDownMyShows = () => async (dispatch: Function, getState: TGetState) => {
    const state = getState();
    const { page, myShowsItems, isPageLoading } = state.myShows;
    const filterList = state.list[ListTypes.MYSHOWS_FILTER];
    const { screenTaskController } = state.screens;

    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS) return;
    if (
        isPageLoading ||
        filterList.isFocused ||
        myShowsItems.totalMyShowsCount <= (page + 1) * MAX_ITEM_IN_PAGE
    )
        return;

    await dispatch(setMyShowsPage(page + 1));
    const highLightIndex = getItemsListHiglightPosition('down', myShowsItems.totalMyShowsCount);
    dispatch(setListEntryPosition(ListTypes.MYSHOWS_ITEMS, highLightIndex));
};

export const onInfoCardDismissMyShows =
    () => async (dispatch: AppDispatch, getState: TGetState) => {
        const {
            hasError,
            myShowsItems: { totalMyShowsCount: totalItemCount },
        } = getState().myShows;
        const infoCard = InfoCardManager.getInstance();
        const contextData = infoCard.getContextData(MyShowsInfoCardUserInputHandler.name) || {};
        const { activeItemIndex, initialPageOffset } = contextData;
        const itemExistInPage =
            activeItemIndex >= initialPageOffset &&
            activeItemIndex <= initialPageOffset + MAX_ITEM_IN_PAGE - 1;

        if (itemExistInPage) {
            dispatch(
                setListEntryPosition(ListTypes.MYSHOWS_ITEMS, activeItemIndex - initialPageOffset),
            );
            dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
        } else {
            let page = Math.floor(activeItemIndex / MAX_ITEM_IN_PAGE);
            let offset = page * MAX_ITEM_IN_PAGE;

            if (MyShowsListGenerator.isListPartial()) {
                const noOfItemsInLastPage = totalItemCount % MAX_ITEM_IN_PAGE;
                const isNotFirstPage = activeItemIndex >= noOfItemsInLastPage;
                const isNotLastPage = page !== Math.floor(totalItemCount / MAX_ITEM_IN_PAGE);
                const isMidPage = isNotFirstPage && isNotLastPage;

                if (MyShowsListGenerator.isLastPageVisited() && isMidPage) {
                    page += 1;
                    offset = page * MAX_ITEM_IN_PAGE - (MAX_ITEM_IN_PAGE - noOfItemsInLastPage);
                }
                if (!isNotLastPage) offset -= MAX_ITEM_IN_PAGE - noOfItemsInLastPage;
            }

            await dispatch(setMyShowsPage(page));
            dispatch(setListEntryPosition(ListTypes.MYSHOWS_ITEMS, activeItemIndex - offset));
            if (!hasError) dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, true));
        }
    };

export const onKeyInfoMyShows = () => (dispatch: AppDispatch, getState: TGetState) => {
    if (
        !CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
        )
    )
        return;

    // TODO : Check Parental control behavior will be handle by screen or by infocard

    const {
        myShows: {
            myShowsItems: { myShows, totalMyShowsCount },
            myShowsFilter,
        },
        list: { [ListTypes.MYSHOWS_FILTER]: filterList, [ListTypes.MYSHOWS_ITEMS]: itemList },
    } = getState();

    if (!itemList.isFocused) return;

    const selectedFilter = myShowsFilter[filterList.position].value;
    const currentItem = myShows[itemList.position];
    const currentItemIndex = MyShowsListGenerator.getAbsoluteIndex(itemList.position);

    const infoCard: IInfoCard = InfoCardManager.getInstance();
    const myShowsInfoCardUserInputHandler = new MyShowsInfoCardUserInputHandler();
    infoCard.addUserInputEventHandler(myShowsInfoCardUserInputHandler);
    infoCard.setProperties({
        hasItemsBefore: currentItemIndex > 0,
        hasItemsAfter: currentItemIndex < totalMyShowsCount - 1,
    });
    infoCard.setContextData(MyShowsInfoCardUserInputHandler.name, {
        activeItemIndex: currentItemIndex,
        filter: selectedFilter,
        totalItemCount: totalMyShowsCount,
        initialPageList: myShows,
        initialPageOffset: MyShowsListGenerator.getAbsoluteIndex(0),
    });

    infoCard.setInfoCardKernel(
        myShowsInfoCardUserInputHandler.getInfoCardKernelForMyshows(currentItem),
    );

    dispatch(setListEntryFocus(ListTypes.MYSHOWS_ITEMS, false));
    infoCard.show();
};

// Mouse actions
const handleFiltersMouseOver = (hasError: boolean) => async (dispatch: Function) => {
    if (hasError) dispatch(setMyShowsErrorAction(false, ''));
    dispatch(setMyShowsItemsLoadingAction(true));
    await dispatch(setLastSelectedItem());
    dispatch(onMouseOverList());
    dispatch(setMyShowsItemsAction(myShowsItemsObj, 0));
    MyShowsListGenerator.reset();
    dispatch(getMyShowsItems(true));
};

const handleCaretMouseOver =
    (value: string, page: number, totalMyShowsCount: number) => (dispatch: Function) => {
        dispatch(setActiveInteractiveComponent(value));

        const shouldShowCaret =
            value === InteractiveComponents.NORTH
                ? page > 0
                : totalMyShowsCount > (page + 1) * MAX_ITEM_IN_PAGE;

        if (shouldShowCaret) {
            dispatch(setMyShowsCaretVisibleAction(true));
        }
    };

const handleDefaultMyShowsTasksMouseOver =
    (value: string, hasError: boolean, page: number, totalMyShowsCount: number) =>
    async (dispatch: Function) => {
        if (value.includes('filters')) {
            await dispatch(handleFiltersMouseOver(hasError));
        } else if (value.includes('items')) {
            dispatch(onMouseOverList());
        } else if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
            dispatch(handleCaretMouseOver(value, page, totalMyShowsCount));
        }
    };

export const onMouseOverMyShows = () => async (dispatch: Function, getState: TGetState) => {
    const {
        myShows: { page, myShowsItems, hasError },
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState();

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS:
            await dispatch(
                handleDefaultMyShowsTasksMouseOver(
                    value,
                    hasError,
                    page,
                    myShowsItems.totalMyShowsCount,
                ),
            );
            break;
        default:
            break;
    }
};

export const onMouseOutMyShows = () => (dispatch: Function, getState: Function) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState() as RootState;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS:
            if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
                dispatch(setMyShowsCaretVisibleAction(false));
            } else if (value.includes('filters') || value.includes('items')) {
                dispatch(onMouseOutList());
            }
            break;
        default:
            break;
    }
};

export const onMouseWheelMyShows =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
            return; // no action needed
        }
        if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS) return;
        if (wheelDeltaY > 0) {
            dispatch(onKeyUpMyShowsOperations());
        } else if (wheelDeltaY < 0) {
            dispatch(onKeyDownMyShowsOperation());
        }
    };

export const onMouseClickMyShows = () => async (dispatch: Function, getState: Function) => {
    const {
        myShows: { page, myShowsItems, isPageLoading, isCaretVisible },
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState() as RootState;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseClickVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseClickVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_MY_SHOWS_TASKS:
            if (value.includes('myshows-items')) {
                dispatch(onKeyOkMyShows());
            }

            if (!isCaretVisible || isPageLoading) return;

            if (value === InteractiveComponents.NORTH) {
                dispatch(onKeyChannelUpMyShows());
                if (page - 1 === 0) dispatch(setMyShowsCaretVisibleAction(false));
            } else if (value === InteractiveComponents.SOUTH) {
                dispatch(onKeyChannelDownMyShows());
                if (myShowsItems.totalMyShowsCount <= (page + 2) * MAX_ITEM_IN_PAGE)
                    dispatch(setMyShowsCaretVisibleAction(false));
            }
            break;
        default:
            break;
    }
};
